/**
 * Token refresh utilities
 *
 * Provides timing logic for token refresh decisions, response validation,
 * and token expiry calculations. Used by session-provider for automatic
 * token refresh management.
 *
 * Key Constants:
 * - TOKEN_REFRESH_INTERVAL: 5 minutes (300,000ms) - How often to attempt refresh
 * - SESSION_COOKIE_MAX_AGE: 20 minutes - Total session lifetime
 *
 * @see __tests__/refresh-token-utils.test.ts - Comprehensive test suite with 65 tests
 * @see __tests__/MOCK_DATA_REFERENCE.md - Mock data and test scenarios
 * @see __tests__/README.md - Session management architecture and flow
 */

import { TOKEN_REFRESH_INTERVAL } from './auth-helper'

export type RefreshTokenResult =
  | { shouldRefresh: true; reason: RefreshReason }
  | { shouldRefresh: false }

export type RefreshReason =
  | 'interval_elapsed'
  | 'near_expiry'
  | 'network_change'
  | 'manual'

/**
 * Determines if token refresh should occur based on last refresh time
 * Internal function - exported only for tests
 */
export function hasRefreshIntervalElapsed(
  lastRefreshTime: number,
  currentTime: number,
  refreshInterval: number = TOKEN_REFRESH_INTERVAL,
): boolean {
  return currentTime - lastRefreshTime >= refreshInterval
}

/**
 * Determines if token is near expiry and should be refreshed
 * Internal function - exported only for tests
 */
export function isNearExpiry(
  expiryTime: number,
  currentTime: number,
  buffer: number = TOKEN_REFRESH_INTERVAL,
): boolean {
  return currentTime >= expiryTime - buffer
}

/**
 * Calculates next refresh time
 * Internal function - exported only for tests
 */
export function calculateNextRefreshTime(
  currentTime: number,
  refreshInterval: number = TOKEN_REFRESH_INTERVAL,
): number {
  return currentTime + refreshInterval
}

/**
 * Validates if a refresh token response is successful
 * @param response - API response containing access token
 * @returns true if response contains valid access token
 */
export function isRefreshResponseValid(response: {
  status: string
  data?: { access_token?: string } | null
}): boolean {
  return (
    response.status === 'Success' &&
    !!response.data?.access_token &&
    response.data.access_token.length > 0
  )
}

/**
 * Extracts and cleans access token from response
 * Removes 'Bearer ' prefix if present (including extra spaces)
 * @param accessToken - Raw access token from API
 * @returns Cleaned access token
 */
export function cleanAccessToken(accessToken: string): string {
  return accessToken.replace(/^Bearer\s+/i, '')
}

/**
 * Determines if token refresh should occur
 * Internal function - exported only for tests
 */
export function shouldRefreshTokenNow(
  lastRefreshTime: number,
  expiryTime: number,
  currentTime: number = Date.now(),
): RefreshTokenResult {
  // Check if near expiry first (higher priority)
  if (isNearExpiry(expiryTime, currentTime)) {
    return { shouldRefresh: true, reason: 'near_expiry' }
  }

  // Check if refresh interval has elapsed
  if (hasRefreshIntervalElapsed(lastRefreshTime, currentTime)) {
    return { shouldRefresh: true, reason: 'interval_elapsed' }
  }

  return { shouldRefresh: false }
}

/**
 * Calculates session expiry from token response
 * Internal function - exported only for tests
 */
export function calculateTokenExpiry(
  expiresIn: number,
  currentTime: number = Date.now(),
): number {
  return currentTime + expiresIn * 1000
}

/**
 * Validates token refresh timing constraints
 * Internal function - exported only for tests
 */
export function canRefreshToken(
  lastRefreshTime: number,
  currentTime: number,
  minInterval: number = 60000, // 1 minute minimum
): boolean {
  return currentTime - lastRefreshTime >= minInterval
}

/**
 * Calculates optimal refresh time based on token expiry
 * Internal function - exported only for tests
 */
export function calculateOptimalRefreshTime(
  issuedAt: number,
  expiryTime: number,
  targetRefreshRatio: number = 0.75, // Refresh at 75% of lifetime
): number {
  const lifetime = expiryTime - issuedAt
  return issuedAt + lifetime * targetRefreshRatio
}
